import {NfcCard, Occupancy, Payment} from "@variocube/app-api";
import {useFlag} from "@variocube/app-ui";
import {
	useDeactivateListener,
	usePlatform,
	usePreventAutoDeactivate,
	View,
	WizardProgress,
	WizardStep,
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";
import {
	Accessibility,
	BookingOccupancyContent,
	BookingPlan,
	BookingUnit,
	Cart,
	getOccupancyBooking,
	PaymentOptions,
	PricingMode,
} from "./booking";
import {PaymentStep} from "./PaymentStep";
import {ProlongBookingCartStep} from "./ProlongBookingCartStep";
import {formatCurrency} from "./utils";

interface ProlongBookingFlowProps {
	plans: BookingPlan[];
	paymentOptions: PaymentOptions;
	onDone: (occupancy?: Occupancy) => void;
	onClearOut: (occupancy: Occupancy) => void;
	accessibleFrom: string;
	accessibleUntil: string;
	accessibility: Accessibility;
	occupancy: Occupancy;
	currency: string;
	bookingUnit: BookingUnit;
	pricingMode: PricingMode;
	className?: string;
}

enum ProlongBookingStep {
	Cart,
	Payment,
}

export function ProlongBookingFlow(props: ProlongBookingFlowProps) {
	const {
		occupancy,
		onDone,
		onClearOut,
		plans,
		paymentOptions,
		accessibleFrom,
		accessibleUntil,
		accessibility,
		currency,
		bookingUnit,
		pricingMode,
		className,
	} = props;
	const [cart, setCart] = useState<Cart>();
	const [zeroAmount, setZeroAmount] = useFlag(false);

	const newOccupancy = useRef<Occupancy>();

	const platform = usePlatform();
	const {t, language} = useLocalization();

	const booking = getOccupancyBooking(occupancy);

	const currentStep = useMemo(() => {
		if (!cart) {
			return ProlongBookingStep.Cart;
		}
		return ProlongBookingStep.Payment;
	}, [cart]);

	usePreventAutoDeactivate();

	const handleDeactivate = useCallback(async () => {
		if (currentStep == ProlongBookingStep.Payment) {
			throw new Error("Deactivate not allowed.");
		}
		onDone();
	}, [currentStep, onDone]);

	useDeactivateListener(handleDeactivate);

	const handlePaymentApproved = useCallback(async (payment: Payment, card?: NfcCard) => {
		// We create a new occupancy that replaces the existing one.
		// This has the benefit that no existing information is overwritten in the backend,
		// however there is currently no link between the original and extended occupancy
		// and the corresponding bookings.
		if (booking && cart) {
			await platform.endOccupancy(occupancy.uuid);

			try {
				const prolongationOccupancy = await platform.occupy({
					boxNumber: occupancy.boxNumber,
					accessCode: occupancy.accessCode,
					content: {
						booking: {
							...cart,
							phone: booking.phone,
							email: booking.email,
							locale: language,
							cardUid: (card && card.uid) || booking.cardUid,
							receiptText: payment.receiptText,
							merchantReceiptText: payment.merchantReceiptText,
							merchantTransactionUrl: payment.merchantTransactionUrl,
							from: cart.from.toISO(),
							until: cart.until.toISO(),
							created: DateTime.local().toISO(),
						},
						// these properties can (later) be used by the backend
						// to link the occupancies and bookings.
						prolongedOccupancyId: occupancy.content.occupancyId,
						prolongedBookingUuid: occupancy.content.bookingUuid,
					} as BookingOccupancyContent,
				});
				await platform.confirmOccupancy(prolongationOccupancy.uuid);
				newOccupancy.current = prolongationOccupancy;
			} catch (error) {
				console.error("Failed to create new occupancy", error);
				// this is very bad, but should not happen in reality.
				// re-throw error to cancel payment
				throw error;
			}
		}
	}, [platform, booking, cart, occupancy]);

	const handlePaymentConfirmationFailed = useCallback(async () => {
		if (newOccupancy.current) {
			// end new occupancy and restore the old one.
			await platform.endOccupancy(newOccupancy.current.uuid);
			newOccupancy.current = undefined;
			const restored = await platform.occupy(occupancy);
			await platform.confirmOccupancy(restored.uuid);
		}
	}, [platform, occupancy]);

	const handlePaymentConfirmed = useCallback(() => {
		if (newOccupancy.current) {
			onDone(newOccupancy.current);
		}
	}, [onDone]);

	return (
		<View className={className}>
			{!zeroAmount && (
				<WizardProgress>
					<WizardStep title>
						<div className={styles.stepName}>{t("ProlongBooking")}</div>
					</WizardStep>
					<WizardStep
						active={currentStep == ProlongBookingStep.Cart}
						done={currentStep > ProlongBookingStep.Cart}
					>
						<div className={styles.stepName}>{t("Duration")}</div>
						{cart && (
							<div className={styles.stepSummary}>
								{`${t("Size")} ${cart.boxType} | ${t(bookingUnit)} ${cart.units}`}
							</div>
						)}
					</WizardStep>
					<WizardStep
						active={currentStep == ProlongBookingStep.Payment}
						done={currentStep > ProlongBookingStep.Payment}
					>
						<div className={styles.stepName}>{t("Payment")}</div>
						{cart && (
							<div className={styles.stepSummary}>
								{t("Amount")} {formatCurrency(cart.amount, currency, language)}
							</div>
						)}
					</WizardStep>
					<WizardStep fill>
					</WizardStep>
				</WizardProgress>
			)}
			{(currentStep == ProlongBookingStep.Cart) && (
				<ProlongBookingCartStep
					platform={platform}
					plans={plans}
					maxAmount={paymentOptions.maxAmount}
					boxNumber={occupancy.boxNumber}
					booking={getOccupancyBooking(occupancy)!}
					accessibleFrom={accessibleFrom}
					accessibleUntil={accessibleUntil}
					accessibility={accessibility}
					onCart={setCart}
					onCancel={onDone}
					onConfirmClearOut={() => onClearOut(occupancy)}
					onZeroAmount={setZeroAmount}
					currency={currency}
					bookingUnit={bookingUnit}
					pricingMode={pricingMode}
				/>
			)}
			{currentStep == ProlongBookingStep.Payment && (
				<PaymentStep
					platform={platform}
					onCancel={onDone}
					amount={cart!.amount}
					currency={currency}
					onPaymentApproved={handlePaymentApproved}
					onPaymentConfirmationFailed={handlePaymentConfirmationFailed}
					onPaymentConfirmed={handlePaymentConfirmed}
					requireCardUid={paymentOptions.requireCardUid}
				/>
			)}
		</View>
	);
}

const styles = stylesheet({
	stepSummary: {
		fontWeight: "bold",
	},
	stepName: {
		textTransform: "uppercase",
	},
});
